import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { listHistory, type HistoryEntry, type HistoryAction } from "../lib/authClient";
import { siteKey } from "../lib/siteData";

// History tab — a shared, read-only log of every successfully-applied action across the account and its
// team seats (new pages, service-area pages, schema, Express internal links, meta updates, redirects,
// low-hanging KW insertions). Backed by /api/history, keyed server-side to the owner group so the
// agency owner and every seat see the same rows. Rendered from a footer button above Settings.

interface Props { siteUrl?: string; refreshToken?: number }

// Canonical action code → display label. Order also drives the filter dropdown.
const ACTION_LABEL: Record<HistoryAction, string> = {
  new_page: "New Page",
  new_service_area_page: "New Service Area Page",
  schema_added: "Schema Added",
  internal_links_added: "Internal Links Added",
  meta_updated: "Updated Meta Data",
  redirect_added: "Redirect Added",
  lhk_added: "Low-Hanging KWs Added",
};
const ACTION_ORDER: HistoryAction[] = ["new_page", "new_service_area_page", "schema_added", "internal_links_added", "meta_updated", "redirect_added", "lhk_added"];
const actionLabel = (a: string) => ACTION_LABEL[a as HistoryAction] ?? a;

const fmtDateTime = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 16) : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };

export function HistoryTab(props: Props) {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const key = props.siteUrl ? siteKey(props.siteUrl) : "";
  const load = useCallback(async () => {
    if (!key) { setEntries([]); setLoaded(true); return; }
    setLoading(true); setError(null);
    try { setEntries(await listHistory(key)); setLoaded(true); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not load history."); }
    finally { setLoading(false); }
  }, [key]);

  useEffect(() => { void load(); }, [load, props.refreshToken]);

  // ── Theme ──
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const inkMid = isDark ? "#cbd5e1" : "#334155";
  const red = isDark ? "#f87171" : "#dc2626";
  const boxBorder = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const accent = "#f97316";
  const brandTitle: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: accent, letterSpacing: ".7px" };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 820 };
  const inp: React.CSSProperties = { boxSizing: "border-box", padding: "6px 10px", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", background: isDark ? "#1a2740" : "#fff", color: ink, outline: "none" };

  // Per-action pill color (foreground + tinted background). Semantic, distinct from the orange accent.
  const pill = (a: string): { fg: string; bg: string } => {
    const map: Record<string, [string, string, string]> = {
      new_page: ["#16a34a", "#86efac", "rgba(34,197,94,.16)"],
      new_service_area_page: ["#0d9488", "#5eead4", "rgba(20,184,166,.16)"],
      schema_added: ["#2563eb", "#93c5fd", "rgba(59,130,246,.16)"],
      internal_links_added: ["#7c3aed", "#c4b5fd", "rgba(139,92,246,.16)"],
      meta_updated: ["#b45309", "#fcd34d", "rgba(245,158,11,.16)"],
      redirect_added: ["#db2777", "#f9a8d4", "rgba(236,72,153,.16)"],
      lhk_added: ["#ea580c", "#fdba74", "rgba(249,115,22,.16)"],
    };
    const m = map[a] ?? ["#64748b", "#cbd5e1", "rgba(148,163,184,.16)"];
    return { fg: isDark ? m[1] : m[0], bg: m[2] };
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e =>
      (actionFilter === "all" || e.action === actionFilter) &&
      (!q || e.url.toLowerCase().includes(q) || e.actorEmail.toLowerCase().includes(q)),
    );
  }, [entries, actionFilter, search]);

  // Present in the data (for the filter dropdown) — keep it to actions that actually appear.
  const presentActions = useMemo(() => ACTION_ORDER.filter(a => entries.some(e => e.action === a)), [entries]);

  // ── Row virtualization (windowing) ─────────────────────────────────────────
  // A long history (hundreds/thousands of rows) is thousands of DOM nodes → the tab froze while the
  // browser built/tore down the table. Render only the rows in/near the viewport, with spacer rows
  // above/below to keep the scrollbar height + position correct. Search/filter still operate on the
  // FULL list — only the rendered DOM is capped.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [rowHeight, setRowHeight] = useState(37);          // measured from a real row after first paint
  const [win, setWin] = useState({ start: 0, end: 60 });   // absolute row indices currently rendered
  const winRafRef = useRef<number | null>(null);
  const OVERSCAN = 12;
  const recomputeWindow = () => {
    const el = scrollRef.current;
    if (!el) return;
    const rh = rowHeight || 37;
    const total = filtered.length;
    const start = Math.max(0, Math.floor(el.scrollTop / rh) - OVERSCAN);
    const count = Math.ceil(el.clientHeight / rh) + OVERSCAN * 2;
    const end = Math.min(total, start + count);
    setWin(prev => (prev.start === start && prev.end === end ? prev : { start, end }));
  };
  const onScrollVertical = () => {
    if (winRafRef.current != null) return;
    winRafRef.current = requestAnimationFrame(() => { winRafRef.current = null; recomputeWindow(); });
  };
  // Measure the real row height after paint, then fit the window to the viewport.
  useLayoutEffect(() => {
    const first = scrollRef.current?.querySelector<HTMLElement>("tbody tr[data-histrow]");
    if (first && first.offsetHeight > 0 && Math.abs(first.offsetHeight - rowHeight) > 1) setRowHeight(first.offsetHeight);
    recomputeWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, rowHeight, loaded]);
  useEffect(() => {
    const onResize = () => recomputeWindow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, rowHeight]);
  // Reset scroll + window to the top whenever the filter/search narrows the list.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setWin({ start: 0, end: 60 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, search, entries]);

  const renderUrl = (u: string) => {
    return <span style={{ color: inkMid, overflowWrap: "anywhere", wordBreak: "break-word" }}>{u}</span>;
  };

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div style={{ padding: "0 9px", marginBottom: 16 }}>
        <div className="cd-hd-brand" style={brandTitle}>History</div>
        <div className="cd-help" style={help}>
          Every change the tool has successfully applied to <strong>the connected website</strong> — new pages,
          service-area pages, schema, internal links (Express tool), meta updates, redirects, and low-hanging keyword
          insertions. Shared with everyone on your account, so you and your team can see who changed what, and when.
          Switch sites in the Site Storage dropdown to see another website&rsquo;s history.
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <button onClick={() => void load()} disabled={loading}
            style={{ padding: "8px 15px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: loading ? "default" : "pointer", background: loading ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap" }}>
            {loading ? "Loading…" : "Refresh"}
          </button>
          {presentActions.length > 0 && (
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
              <option value="all">All actions</option>
              {presentActions.map(a => <option key={a} value={a}>{ACTION_LABEL[a]}</option>)}
            </select>
          )}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search URL or user…" style={{ ...inp, flex: "1 1 200px", minWidth: 160, maxWidth: 320 }} />
          {loaded && <span style={{ fontSize: 11.5, color: inkMuted, marginLeft: "auto" }}>{filtered.length} of {entries.length} action{entries.length === 1 ? "" : "s"}</span>}
        </div>
      </div>

      {error && <div style={{ padding: "0 9px", marginBottom: 12, fontSize: 12.5, color: red }}>{error}</div>}

      <div style={{ padding: "0 9px" }}>
        {!key ? (
          <p className="cd-help">Select a website (Site Storage dropdown) to see its history.</p>
        ) : !loaded && loading ? (
          <p className="cd-help">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="cd-help">No actions recorded yet for this website — applied changes (new pages, schema, internal links, meta updates, redirects, keyword insertions) will appear here.</p>
        ) : filtered.length === 0 ? (
          <p className="cd-help">No actions match the current filter.</p>
        ) : (
          <div ref={scrollRef} onScroll={onScrollVertical} style={{ overflow: "auto", maxHeight: "70vh" }}>
            <table className="cd-history" style={{ margin: 0, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>Date</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>Action</th>
                  <th style={{ textAlign: "left", position: "sticky", top: 0, zIndex: 2 }}>URL</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>User</th>
                </tr>
              </thead>
              <tbody>
                {win.start > 0 && (
                  <tr aria-hidden="true"><td colSpan={4} style={{ height: win.start * rowHeight, padding: 0, border: "none" }} /></tr>
                )}
                {filtered.slice(win.start, win.end).map(e => {
                  const p = pill(e.action);
                  return (
                    <tr key={e.id} data-histrow="">
                      <td style={{ fontSize: 11.5, color: ink2, whiteSpace: "nowrap" }}>{fmtDateTime(e.createdAt)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: p.fg, background: p.bg, border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "2px 9px" }}>{actionLabel(e.action)}</span>
                      </td>
                      <td style={{ fontSize: 11.5 }}>{renderUrl(e.url)}</td>
                      <td style={{ fontSize: 11.5, color: inkMid, whiteSpace: "nowrap" }}>{e.actorEmail}</td>
                    </tr>
                  );
                })}
                {win.end < filtered.length && (
                  <tr aria-hidden="true"><td colSpan={4} style={{ height: (filtered.length - win.end) * rowHeight, padding: 0, border: "none" }} /></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
